import { addSentryBreadcrumb } from '../../shared/sentry/sentryUser';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { formatMoney } from '../../shared/format/public-api';
import { applyCustomerViaRpc } from '../../sync/core/flushRpc';
import type { SyncQueueItem } from '../../sync/core/syncTypes';
import { invalidateBranchSalesListCaches, invalidateSaleDetailCache } from '../../sales/infra/salesListInvalidation';
import {
  bumpCashSessionChangeSignal,
  invalidateAllCashSummaryReportsCaches,
  invalidateCashSummaryCaches,
} from '../../cash/infra-api';
import {
  getCustomerSalesForCustomerFromRemote,
  refreshWebCustomerSnapshotAfterWrite,
  shouldReadCustomerFromRemoteOnWeb,
} from './customerRepositoryShared';
import { adjustWebCustomerBalanceInCache } from './webCustomerSnapshotCache';

export type SalePaymentAllocationSyncRow = {
  id: string;
  saleId: string;
  amount: number;
  createdAt: string;
};

/** Medio de cobro aceptado por apply_customer_command en cobros de cuenta. */
export type CustomerPaymentMethodId = 'cash' | 'transfer' | 'card' | 'mercadopago_qr' | 'cheque';

/** Datos del cheque (paymentMethod='cheque'); number es requerido server-side. */
export type CustomerCheckInput = {
  number: string;
  dueDate?: string;
  drawer?: string;
  bank?: string;
  /** Solo multi-cheque (checks[]): sin amount el server usa el total del cobro. */
  amount?: number;
};

/**
 * Obtiene ventas customer de un cliente para el dropdown de vinculación.
 * Retorna opciones ordenadas por fecha descendente (más recientes primero).
 */
export const getCustomerSalesForCustomer = async (customerId: string): Promise<
  Array<{
    id: string;
    shortId: string;
    createdAt: string;
    total: number;
    paidAmount: number;
    pendingAmount: number;
  }>
> => {
  if (shouldReadCustomerFromRemoteOnWeb()) {
    return getCustomerSalesForCustomerFromRemote(customerId);
  }
  // ADR-017: online-only — sin SQLite. Native sin remote path aún → vacío.
  return [];
};

export async function buildPaymentAllocations(
  customerId: string,
  amount: number,
  saleId: string | null | undefined
): Promise<SalePaymentAllocationSyncRow[]> {
  const createdAt = new Date().toISOString();
  if (saleId) {
    return [{ id: crypto.randomUUID(), saleId, amount, createdAt }];
  }

  const sales = (await getCustomerSalesForCustomer(customerId)).slice().reverse();
  let remaining = amount;
  const rows: SalePaymentAllocationSyncRow[] = [];
  for (const sale of sales) {
    if (remaining <= 0) break;
    const pending = Number(sale.pendingAmount ?? 0);
    if (pending <= 0) continue;
    const allocated = Math.min(remaining, pending);
    rows.push({ id: crypto.randomUUID(), saleId: sale.id, amount: allocated, createdAt });
    remaining -= allocated;
  }
  return rows;
}

function invalidateSalesCachesAfterPayment(
  branchId: string | null | undefined,
  allocationRows: SalePaymentAllocationSyncRow[],
): void {
  const branchKey = branchId?.trim();
  if (branchKey) {
    invalidateBranchSalesListCaches(branchKey);
  }
  for (const alloc of allocationRows) {
    invalidateSaleDetailCache(alloc.saleId);
  }
}

/** Persiste el pago vía apply_customer_command (online-only). */
async function recordCustomerPaymentViaServer(params: {
  customerId: string;
  amount: number;
  note?: string;
  performedByUserId?: string | null;
  originDeviceId?: string | null;
  branchId?: string | null;
  saleId?: string | null;
  movementId?: string | null;
  paymentMethod?: CustomerPaymentMethodId | null;
  check?: CustomerCheckInput | null;
  /** Multi-cheque: array completo. Emite también `check` = primero (back-compat). */
  checks?: CustomerCheckInput[] | null;
}): Promise<{ movementId: string; allocationRows: SalePaymentAllocationSyncRow[] }> {
  const movementId = params.movementId?.trim() || crypto.randomUUID();
  const createdAt = new Date().toISOString();
  const allocationRows = await buildPaymentAllocations(params.customerId, params.amount, params.saleId);

  const syncPayload: Record<string, unknown> = {
    customerId: params.customerId,
    movementId,
    amount: params.amount,
    type: 'payment',
    note: params.note,
    branchId: params.branchId ?? undefined,
    saleId: params.saleId ?? undefined,
    paymentMethod: params.paymentMethod ?? undefined,
    check: params.check ?? params.checks?.[0] ?? undefined,
    checks: params.checks ?? undefined,
    createdAt,
    performedByUserId: params.performedByUserId ?? null,
    originDeviceId: params.originDeviceId ?? null,
    operationId: movementId,
    allocations: allocationRows.map((row) => ({
      id: row.id,
      saleId: row.saleId,
      amount: row.amount,
      createdAt: row.createdAt,
    })),
  };

  const queueItem: SyncQueueItem = {
    id: crypto.randomUUID(),
    entity: 'fiado',
    operation: 'update',
    payload: syncPayload,
    status: 'pending',
    created_at: createdAt,
  };

  const pushResult = await applyCustomerViaRpc(queueItem);
  if (!pushResult.ok) {
    const error = new Error(pushResult.error);
    captureOpsFailure({
      module: 'customers',
      op: 'customer.payment',
      error,
      ingestError: pushResult.error,
      applied: false,
      path: 'web-ram',
      itemId: params.customerId,
      extra: { customerId: params.customerId },
    });
    throw error;
  }

  invalidateSalesCachesAfterPayment(params.branchId, allocationRows);
  // El cobro impacta la sesión de caja activa (efectivo al esperado, otros
  // medios al breakdown) — invalidar para que el arqueo lo refleje.
  if (params.branchId?.trim()) {
    invalidateCashSummaryCaches(params.branchId.trim());
  } else {
    invalidateAllCashSummaryReportsCaches();
  }
  bumpCashSessionChangeSignal();
  return { movementId, allocationRows };
}

export const recordCustomerPayment = async ({
  customerId,
  amount,
  note,
  performedByUserId,
  originDeviceId,
  branchId,
  saleId,
  movementId: preferredMovementId,
  paymentMethod,
  check,
  checks,
}: {
  customerId: string;
  amount: number;
  note?: string;
  performedByUserId?: string | null;
  originDeviceId?: string | null;
  /** Sucursal activa (Informes / resúmenes por sucursal). */
  branchId?: string | null;
  /** Venta específica a vincular (opcional). Si no se especifica, usa asignación FIFO automática. */
  saleId?: string | null;
  /** Idempotencia: reusar en retries del mismo submit. */
  movementId?: string | null;
  /** Medio de cobro. Null = comportamiento legacy (no toca caja). */
  paymentMethod?: CustomerPaymentMethodId | null;
  /** Datos del cheque (paymentMethod='cheque'). */
  check?: CustomerCheckInput | null;
  /** Multi-cheque (Fase C): varios cheques en un mismo cobro. */
  checks?: CustomerCheckInput[] | null;
}) => {
  addSentryBreadcrumb('customers', `Pago customer registrado: ${formatMoney(amount)}`, 'info', {
    customerId,
    amount,
    saleId: saleId ?? null,
    paymentMethod: paymentMethod ?? null,
  });

  await recordCustomerPaymentViaServer({
    customerId,
    amount,
    note,
    performedByUserId,
    originDeviceId,
    branchId,
    saleId,
    movementId: preferredMovementId,
    paymentMethod,
    check,
    checks,
  });

  // Audit server-side en apply_customer_command (no duplicar desde el cliente).
  adjustWebCustomerBalanceInCache(customerId, -amount);
  await refreshWebCustomerSnapshotAfterWrite();
};
